import color from 'color';
import type {
  ColorValue,
  GestureResponderEvent,
  StyleProp,
  TextStyle,
  ViewStyle
} from 'react-native';
import { StyleSheet, View } from 'react-native';
import { Text, TouchableRipple } from 'react-native-paper';

import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

export type SegmentButtonTheme = {
  colors?: Partial<
    Pick<
      AppTheme['colors'],
      | 'secondaryContainer'
      | 'onSecondaryContainer'
      | 'outline'
      | 'onSurface'
      | 'surfaceDisabled'
      | 'onSurfaceDisabled'
    >
  >;
  roundness?: number;
};

export type SegmentedButtonItemProps = {
  checked: boolean;
  label?: string;
  onPress: (event: GestureResponderEvent) => void;
  segment?: 'first' | 'last';
  style?: StyleProp<ViewStyle>;
  labelStyle?: StyleProp<TextStyle>;
  rippleStyle?: StyleProp<ViewStyle>;
  rippleColor?: ColorValue;
  testID?: string;
  theme?: SegmentButtonTheme;
};

const pickedColors = (theme: AppTheme): SegmentButtonTheme['colors'] => ({
  secondaryContainer: theme.colors.secondaryContainer,
  onSecondaryContainer: theme.colors.onSecondaryContainer,
  outline: theme.colors.outline,
  onSurface: theme.colors.onSurface,
  surfaceDisabled: theme.colors.surfaceDisabled,
  onSurfaceDisabled: theme.colors.onSurfaceDisabled
});

const makeStyles = () =>
  StyleSheet.create({
    button: {
      flex: 1,
      minWidth: 76,
      borderStyle: 'solid'
    },
    content: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 9,
      paddingHorizontal: 16
    },
    label: {
      textAlign: 'center'
    },
    ripple: {
      flex: 1,
      overflow: 'hidden'
    }
  });

export const SegmentedButtonItem = ({
  checked,
  label,
  onPress,
  segment,
  style,
  labelStyle,
  rippleStyle,
  rippleColor,
  testID,
  theme: themeOverrides
}: SegmentedButtonItemProps) => {
  const appTheme = useAppTheme();
  const styles = makeStyles();

  const roundness = themeOverrides?.roundness ?? appTheme.roundness;
  const colors = {
    ...pickedColors(appTheme),
    ...themeOverrides?.colors
  };

  const borderRadius = 5 * roundness;
  let segmentBorderRadius: ViewStyle = {};
  if (segment === 'first') {
    segmentBorderRadius = {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      borderEndWidth: 0
    };
  } else if (segment === 'last') {
    segmentBorderRadius = {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0
    };
  } else {
    segmentBorderRadius = {
      borderRadius: 0,
      borderEndWidth: 0
    };
  }

  const backgroundColor = checked ? colors.secondaryContainer : 'transparent';
  const borderColor = colors.outline;
  const borderWidth = 1;
  const textColor = checked ? colors.onSecondaryContainer : colors.onSurface;

  const defaultRipple = color(textColor).alpha(0.12).rgb().string();

  const buttonStyle: ViewStyle = {
    backgroundColor,
    borderColor,
    borderWidth,
    borderRadius,
    ...segmentBorderRadius
  };
  const labelTextStyle = {
    color: textColor
  };

  return (
    <TouchableRipple
      borderless
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ checked }}
      rippleColor={rippleColor ?? defaultRipple}
      style={[buttonStyle, styles.button, style, styles.ripple, rippleStyle]}
      testID={testID}
    >
      <View style={styles.content}>
        <Text
          variant="labelLarge"
          style={[styles.label, labelTextStyle, labelStyle]}
          selectable={false}
          numberOfLines={1}
        >
          {label}
        </Text>
      </View>
    </TouchableRipple>
  );
};
